/**
 * Times, in one place.
 *
 * Everything on the wire is an ISO instant in UTC. Everything on screen is in the
 * reader's own timezone. The two conversions below are the only places that crosses over,
 * because doing it ad hoc is how a target time set at 16:00 in Doha comes back as 13:00
 * and nobody can say which of the two the record actually means.
 */

const pad = (n) => String(n).padStart(2, '0');

/**
 * An instant, formatted for a datetime-local input.
 *
 * Not toISOString().slice(0,16) — that is the UTC clock, and putting it in a control the
 * browser labels as local time silently shifts every value by the offset. The parts have
 * to come from the local getters.
 */
export function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The reverse: what a datetime-local input gives back, as a UTC instant. */
export function fromLocalInput(value) {
  if (!value) return null;
  const d = new Date(value); // parsed as local time, which is what the control means
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export const clock = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

export const clockSeconds = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

/** Day and time. Used wherever a value may be more than a few hours away from now. */
export const stamp = (iso) => new Date(iso).toLocaleString([], {
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
});

/** "2h 14m" — how long since, or between. */
export function since(from, to) {
  const ms = (to ? new Date(to) : new Date()) - new Date(from);
  const h = Math.floor(ms / 3600000), m = Math.round((ms % 3600000) / 60000);
  return h ? `${h}h ${m}m` : `${m}m`;
}

/** "in 40m" / "1h 5m late" — a target time said the way somebody would say it out loud. */
export function relativeToNow(iso) {
  const ms = new Date(iso) - new Date();
  const late = ms < 0;
  const abs = Math.abs(ms);
  const h = Math.floor(abs / 3600000), m = Math.round((abs % 3600000) / 60000);
  const span = h ? `${h}h ${m}m` : `${m}m`;
  return late ? `${span} late` : `in ${span}`;
}
